import { ReactNode, useEffect, useRef, useState } from 'react'
import { PaginationBar } from '@systemeio/components'
import { useRouter } from 'next/router'
import { OrderEnum, PaginationInterface } from 'shared/types/swr-pagination-interface'
import EmptyList from '../../profile/manage-subscriptions/components/table/empty-list'
import { useTemplates } from '../hooks/use-templates'
import { TemplatesFilterInterface, TemplatesLimitType } from '../types/templates-filter-interface'
import TemplatesListItem from './templates-list-item'
import TemplatesListSkeleton from './templates-list-skeleton'

interface TemplatesListProps {
  filter: TemplatesFilterInterface
  onChangeCategory: (category: number | null) => void
}

const TemplatesList = ({ filter, onChangeCategory }: TemplatesListProps): ReactNode => {
  const { locale } = useRouter()

  const [pagination, setPagination] = useState<PaginationInterface>({
    startFromId: undefined,
    order: OrderEnum.next,
  })

  const [limit, setLimit] = useState<TemplatesLimitType>(15)

  const { templates } = useTemplates({ limit, pagination, filter })
  const templatesRef = useRef<HTMLDivElement>(null)

  const [isFirst, setIsFirst] = useState(true)

  const [isLast, setIsLast] = useState(false)

  const [isPaginationShown, setIsPaginationShown] = useState(true)

  useEffect(() => {
    setPagination({ order: OrderEnum.next, startFromId: undefined })
    setLimit(15)
  }, [filter, locale, setLimit, setPagination])

  useEffect(() => {
    if (!isFirst && templates?.items.length === 0) {
      setPagination({ order: OrderEnum.next })
    }
  }, [templates?.items.length, isFirst, setPagination])

  useEffect(() => {
    if (templates) {
      setIsFirst(true)
      setIsLast(false)

      switch (pagination.order) {
        case 'next': {
          if (pagination.startFromId) {
            setIsFirst(false)
          }
          if (!templates.hasMore) {
            setIsLast(true)
          }
          break
        }
        case 'prev': {
          if (templates.hasMore) {
            setIsFirst(false)
          }
        }
      }
    }
  }, [templates, pagination.order, pagination.startFromId])

  useEffect(() => {
    if (templates) {
      setIsPaginationShown(true)

      if (!isFirst || limit !== 15) return

      if (pagination.order === 'next' && !templates.hasMore) {
        setIsPaginationShown(false)
      }
      if (templates.items.length === 0) {
        setIsPaginationShown(false)
      }
    } else {
      setIsPaginationShown(false)
    }
  }, [templates, isFirst, limit, pagination.order, pagination.startFromId])

  const scrollToTemplates = (): void => {
    if (templatesRef.current) {
      templatesRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return templates ? (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {templates.items.length > 0 ? (
        <div
          ref={templatesRef}
          className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-8 xl:grid-cols-3 xl:gap-8"
        >
          {templates.items.map(template => (
            <TemplatesListItem
              selectedCategory={filter.category}
              onChangeCategory={onChangeCategory}
              key={template.id}
              template={template}
            />
          ))}
        </div>
      ) : (
        <EmptyList />
      )}
      {isPaginationShown && (
        <PaginationBar
          hasNextPage={!isLast}
          hasPreviousPage={!isFirst}
          limit={limit}
          onNextPage={(): void => {
            setPagination({
              startFromId: templates.items.at(-1)?.id,
              order: OrderEnum.next,
            })
            scrollToTemplates()
          }}
          onPreviousPage={(): void => {
            setPagination({
              startFromId: templates.items[0]?.id,
              order: OrderEnum.prev,
            })
            scrollToTemplates()
          }}
          onLimitChange={(nextLimit: number): void => {
            setLimit(nextLimit as TemplatesLimitType)
            setPagination({
              startFromId: undefined,
              order: OrderEnum.next,
            })
            scrollToTemplates()
          }}
          selectProps={{ options: [15, 30, 60] }}
        />
      )}
    </div>
  ) : (
    <TemplatesListSkeleton />
  )
}

export default TemplatesList
